import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import {AnimatePresence, motion} from 'framer-motion';

// --- New Custom Logo Component (with the fix) ---
const Logo = () => (
    <Link to="/" className="flex items-center space-x-2">
        <motion.div
            className="w-10 h-10 flex items-center justify-center"
            initial={{ rotate: -180, scale: 0 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        >
            {/* The SVG combines a magnifying glass and a checkmark */}
            <svg width="100%" height="100%" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 39C31.4934 39 40 30.4934 40 20C40 9.50659 31.4934 1 21 1C10.5066 1 2 9.50659 2 20C2 30.4934 10.5066 39 21 39Z" stroke="#4F46E5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M33.2216 33.2217L45.9998 46" stroke="#4F46E5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <motion.path
                    d="M14 20L19 25L28 16"
                    stroke="#34D399"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.5, delay: 0.5, ease: 'easeInOut' }}
                />
            </svg>
        </motion.div>
        {/* --- FIX: Removed 'hidden' and 'sm:inline-block' classes --- */}
        <span className="text-2xl font-bold text-gray-800">QA Hub</span>
    </Link>
);

// --- Navigation Links (CTA moved out) ---
const navLinks = [
    { name: 'About QA', to: '/about' },
    { name: 'Tutorials', to: '/tutorials' },
    { name: 'Automation', to: '/automation-learning' },
    { name: 'Automation Practice', to: '/automation-practice' },
    { name: 'Tools', to: '/tools' },
    { name: 'Quizzes', to: '/quizzes' },
    { name: 'Interview Prep', to: '/interview-questions' },
    { name: 'Resources', to: '/resources' },
    { name: 'Glossary', to: '/glossary' },
    { name: 'Resume Helper', to: '/resume-helper' },
     // <-- Add this line
    // <-- Add this line
];


const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);

    // More pronounced active style
    const activeLinkStyle = "bg-indigo-100 text-indigo-600";
    const baseLinkStyle = "text-gray-500 hover:text-gray-900 hover:bg-gray-100";

    return (
        <nav className="bg-white/90 backdrop-blur-lg shadow-sm sticky top-0 z-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                        <Logo />
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-1">
                        {navLinks.map((link) => (
                            <NavLink
                                key={link.name}
                                to={link.to}
                                className={({ isActive }) =>
                                    `px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${isActive ? activeLinkStyle : baseLinkStyle}`
                                }
                            >
                                {link.name}
                            </NavLink>
                        ))}
                    </div>

                    {/* Desktop CTA and Mobile Menu Button */}
                    <div className="flex items-center gap-x-4">
                        <div className="hidden md:block">
                            <Link
                                to="/getting-started"
                                className="px-5 py-2.5 rounded-md text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors duration-300"
                            >
                                Get Started
                            </Link>
                        </div>
                        <div className="flex md:hidden">
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                type="button"
                                className="bg-white inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                aria-controls="mobile-menu"
                                aria-expanded="false"
                            >
                                <span className="sr-only">Open main menu</span>
                                {isOpen ? <XMarkIcon className="block h-6 w-6" /> : <Bars3Icon className="block h-6 w-6" />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="md:hidden border-t border-gray-200"
                        id="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                            {navLinks.map((link) => (
                                <NavLink
                                    key={link.name}
                                    to={link.to}
                                    onClick={() => setIsOpen(false)}
                                    className={({ isActive }) =>
                                        `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50'}`
                                    }
                                >
                                    {link.name}
                                </NavLink>
                            ))}
                            <div className="pt-4 px-2">
                                <Link
                                    to="/getting-started"
                                    onClick={() => setIsOpen(false)}
                                    className="block w-full text-center px-5 py-2.5 rounded-md text-base font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm"
                                >
                                    Get Started
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;